// src/components/ui/ListeDense.tsx — SCE Issues
// Primitives de liste tabulaire, pour la couche bureau des listes.
//
// ─── Pourquoi une primitive plutôt qu'une <table> ───
//
// Les colonnes sont fixées par la page, pas par le contenu : on veut que la
// criticité d'une ligne tombe exactement sous celle de la ligne du dessus,
// quelle que soit la longueur du titre. Une <table> recalcule ses largeurs
// d'après les cellules et fait danser les colonnes d'une liste à l'autre.
// Une grille CSS avec un gabarit explicite tient cette promesse.
//
// ─── Pourquoi deux composants et pas un ───
//
// Le gabarit de colonnes doit être identique entre l'en-tête et les rangées,
// sous peine de désalignement invisible au typecheck. Il est donc déclaré une
// fois par la page et passé aux deux, plutôt que recopié.
//
// ⚠️ Ces composants ne sont montés qu'au-delà de 768px (useEstBureau). Sous ce
// seuil les listes restent en cartes : une rangée d'une ligne est illisible et
// intouchable au pouce.

import type { CSSProperties, ReactNode } from 'react'

// ─── Conteneur ───

/**
 * Enveloppe bordée d'une liste dense.
 *
 * Porte la bordure et le rayon pour TOUTE la liste : les rangées ne sont
 * séparées que par un trait de 1px. Empiler des surfaces bordées à cette
 * densité redécouperait l'écran en confettis.
 */
export function ListeDense({ children }: { children: ReactNode }): JSX.Element {
  return <div className="liste-dense">{children}</div>
}

// ─── Rangée ───

interface RangeeDenseProps {
  /** Valeur de `grid-template-columns`. Doit être identique à celle de l'en-tête. */
  gabarit: string
  children: ReactNode
  /** Absent sur une rangée d'en-tête. */
  onClick?: () => void
  /** Rangée de libellés de colonnes : ni cliquable, ni focusable. */
  entete?: boolean
  /** Décalage à gauche, quand la première colonne est un liseré collé au bord. */
  paddingLeft?: number
}

/**
 * Une rangée de la liste.
 *
 * Le clavier est géré comme dans `Card` (Entrée et Espace), pour que les deux
 * rendus d'une même liste — cartes sous 768px, rangées au-delà — se
 * comportent pareil.
 */
export function RangeeDense({
  gabarit,
  children,
  onClick,
  entete,
  paddingLeft = 14,
}: RangeeDenseProps): JSX.Element {
  const interactive = typeof onClick === 'function' && !entete

  const style: CSSProperties = {
    gridTemplateColumns: gabarit,
    paddingLeft,
  }

  return (
    <div
      className={entete ? 'rangee-dense rangee-dense--entete' : 'rangee-dense'}
      style={style}
      onClick={interactive ? onClick : undefined}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onClick?.()
              }
            }
          : undefined
      }
    >
      {children}
    </div>
  )
}

// ─── Cellule ───

interface CelluleProps {
  children?: ReactNode
  /** Coupe le texte à une ligne plutôt que de le replier. */
  tronquee?: boolean
  /** Gris, taille info — pour les données de contexte (OF, date, personne). */
  secondaire?: boolean
  /** Chiffres tabulaires — identifiants, dates, compteurs. */
  chiffres?: boolean
  forte?: boolean
  droite?: boolean
  entete?: boolean
  /**
   * Texte complet en infobulle.
   *
   * Contrepartie directe de la troncature : le titre coupé reste consultable
   * au survol, sans ouvrir la fiche. C'est ce qui rend le compromis tenable.
   */
  titreComplet?: string
}

/** Cellule d'une rangée. Les variantes sont des drapeaux, pas des classes à retenir. */
export function Cellule({
  children,
  tronquee,
  secondaire,
  chiffres,
  forte,
  droite,
  entete,
  titreComplet,
}: CelluleProps): JSX.Element {
  const classes = ['cellule-dense']
  if (entete) classes.push('cellule-dense--entete')
  if (secondaire) classes.push('cellule-dense--secondaire')
  if (tronquee) classes.push('cellule-dense--tronquee')
  if (chiffres) classes.push('cellule-dense--chiffres')
  if (forte) classes.push('cellule-dense--forte')
  if (droite) classes.push('cellule-dense--droite')

  return (
    <span className={classes.join(' ')} title={titreComplet}>
      {children}
    </span>
  )
}

// ─── Squelette de chargement ───

/**
 * Attente d'une liste dense.
 *
 * Reprend la hauteur de rangée et la bordure du conteneur réel : au moment où
 * les données arrivent, rien ne saute. Un rectangle gris de la hauteur d'une
 * carte, lui, ferait s'effondrer la page de plusieurs centaines de pixels.
 */
export function SqueletteDense({ lignes = 8 }: { lignes?: number }): JSX.Element {
  return (
    <ListeDense>
      {Array.from({ length: lignes }).map((_, i) => (
        <div
          key={i}
          className="rangee-dense"
          style={{ gridTemplateColumns: '1fr', cursor: 'default', paddingLeft: 14 }}
        >
          <span
            className="animate-pulse"
            style={{
              height: 12,
              borderRadius: 3,
              backgroundColor: 'var(--color-gray-10)',
              // Largeurs inégales : une pile de barres identiques ne ressemble
              // à aucune liste réelle et se lit comme un défaut d'affichage.
              width: `${[82, 64, 74, 58, 88, 70, 62, 78][i % 8]}%`,
            }}
          />
        </div>
      ))}
    </ListeDense>
  )
}
